import { Alert, Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { Stack } from '@mui/joy';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../../api/auth/authSlice';
import { useGetWebhookStatusHandler } from '../dataHandlers/webhookStatusHandler';
import { useAuthorizeShopifyHandler } from '../dataHandlers/authorizeShopifyHandler';
import logo from '../../../assets/logo/marmin_logo.png';

export const LoginShopify = () => {
  const [queryParams] = useSearchParams();
  const [orderEvent, setOrderEvent] = useState('ORDERS_CREATE');
  const enableShopifyIntegration = true;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const shopId = queryParams.get('shop_id');
  const dispatch = useDispatch();
  const token = queryParams.get('token');
  const authorizationCode = queryParams.get('authorization_code');

  const { webhookRegistered, isLoading, isFetching } = useGetWebhookStatusHandler(shopId, token);

  const processLogin = useCallback(() => {
    localStorage.setItem('AUTH_TOKEN', token);
    dispatch(loginSuccess());
    navigate(`/dashboard`, { replace: true });
  }, [dispatch, navigate, token]);

  useEffect(() => {
    if (webhookRegistered) {
      processLogin();
    }
  }, [processLogin, webhookRegistered]);

  const handleCancel = () => {
    if (!shopId) return;
    window.location.href = `https://admin.shopify.com/store/${shopId.split('.')[0]}`;
  };

  const { onSubmit, isPending } = useAuthorizeShopifyHandler({ onSuccess: processLogin });

  const handleSubmit = () => {
    onSubmit({ shopId, authorizationCode, orderEvent, token });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Stack sx={{ textAlign: 'center', mb: '20px', backgroundColor: '#f5f5f5' }}>
        <img src={logo} alt="Marmin Logo" style={{ maxWidth: '250px', maxHeight: '150px' }} />
      </Stack>
      <Stack
        sx={{
          justifyContent: 'center',
          padding: '20px',
          display: 'flex',
          width: '30%',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h5" sx={{ marginBottom: '20px', fontWeight: 300 }}>
          When would you like to sync the invoice to Marmin?
        </Typography>

        {!enableShopifyIntegration && (
          <Alert severity="error" sx={{ fontSize: '16px', marginBottom: '20px' }}>
            <>
              Shopify Integration is <strong>not enabled</strong> for
              <strong>{'vm@marmin.ai'}</strong> account. Please contact
              <a href="mailto:support@marmin.ai" style={{ marginLeft: '5px', marginRight: '5px' }}>
                support@marmin.ai
              </a>
              for assistance.
            </>
          </Alert>
        )}

        <Stack>
          <FormControl fullWidth sx={{ marginBottom: '16px' }}>
            <InputLabel>Webhook Event</InputLabel>
            <Select
              value={orderEvent}
              label="Webhook event"
              onChange={(e) => setOrderEvent(e.target.value)}
              required
              disabled={!enableShopifyIntegration}
            >
              <MenuItem value="ORDERS_CREATE">Order Create</MenuItem>
              <MenuItem value="ORDERS_PAID">Order Paid</MenuItem>
            </Select>
          </FormControl>

          <div
            style={{
              marginBottom: '1rem',
              marginTop: '0rem',
              justifyContent: 'space-between',
              fontSize: '1rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: 'rgba(70, 80, 110, 0.7)',
              fontFamily: "'Open Sans', sans-serif'",
            }}
          >
            <label>
              Shopify store:
              <strong style={{ color: 'black', fontSize: '1rem' }}>{shopId.split('.')[0]}</strong>
            </label>
          </div>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, marginTop: '20px' }}>
            <Button variant="outlined" color="neutral" size="small" onClick={handleCancel} sx={{ fontSize: '1rem' }}>
              Cancel
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleSubmit}
              sx={{
                color: 'white',
                fontSize: '1rem',
                backgroundColor: '#2a3aff',
                '&:hover': {
                  backgroundColor: '#212ece',
                },
              }}
              disabled={!enableShopifyIntegration || loading}
            >
              Proceed
            </Button>
          </Box>
        </Stack>
      </Stack>

      {(loading || enableShopifyIntegration === undefined || isLoading || isFetching || isPending) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};
